// The Details panel's Spark-specific rail values. The rail frame and the plain editors are
// ui/PropRail; here live the spice meter, the event-owned dates and the drone counts.

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { businessToday, parseDate } from '../utils/date-utils';
import { IconArrowUpRight, IconPencil } from '../components/ui/icons';
import { DateValue as KitDateValue, INLINE_VALUE } from '../components/ui/PropRail';
import { CONTROL_MOTION, FOCUS, STATE, TOUCH } from '../components/ui/motion';
import Field from '../components/ui/Field';
import Tooltip from '../components/ui/Tooltip';
import { SpiceMeter, flamePip } from './SparkBits';
import { SPICE } from './sparkRules';
import { dayText } from './sparkUi';

// ── Moments ──────────────────────────────────────────────────────────────────

/**
 * True from the render where `on` turns true after mount, until it turns false. For a mark
 * that should draw itself when a state is reached, and not on every page load.
 */
export function useBecame(on: boolean): boolean {
  const prev = useRef(on);
  const became = useRef(false);
  if (on !== prev.current) {
    became.current = on;
    prev.current = on;
  }
  return became.current;
}

// ── Days ─────────────────────────────────────────────────────────────────────

export const daysUntil = (ymd: string): number => parseDate(ymd).diff(businessToday(), 'day') as number;

/** "in 3 days", "today", "11 days overdue". Past dates only count as overdue on open work. */
export const whenText = (ymd: string, open: boolean): { text: string; overdue: boolean } => {
  const n = daysUntil(ymd);
  if (n === 0) return { text: 'today', overdue: false };
  if (n === 1) return { text: 'tomorrow', overdue: false };
  if (n > 1) return { text: `in ${n} days`, overdue: false };
  if (!open) return { text: n === -1 ? 'yesterday' : `${-n} days ago`, overdue: false };
  return { text: `${-n} ${n === -1 ? 'day' : 'days'} overdue`, overdue: true };
};

// ── Spice ────────────────────────────────────────────────────────────────────

const PIP_ON = ['', 'bg-orange-500', 'bg-orange-500', 'bg-orange-500', 'bg-red-500', 'bg-rose-600'];

/** The level's one word, beside the pips. Its sentence lives in the pip's tooltip. */
export const SpiceText: React.FC<{ level: number }> = ({ level }) => {
  const s = SPICE.find((x) => x.level === level);
  return s
    ? <span className="min-w-0 truncate text-sm font-medium text-on-surface">{s.label}</span>
    : <span className="min-w-0 truncate text-sm text-on-surface-variant">Not set</span>;
};

/**
 * The meter is the control. Arrow keys move and select, but a run of arrows saves once
 * after a short pause, so stepping from Mild to Fire is one request, not three.
 */
export const SpiceRadio: React.FC<{ level: number; onChange: (level: number) => void }> = ({ level, onChange }) => {
  const [draft, setDraft] = useState(level);
  const [peek, setPeek] = useState<number | null>(null);
  const pending = useRef<number | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const latest = useRef({ level, onChange });
  latest.current = { level, onChange };
  const pips = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => { setDraft(level); }, [level]);

  const flush = () => {
    window.clearTimeout(timer.current);
    const n = pending.current;
    pending.current = null;
    if (n !== null && n !== latest.current.level) latest.current.onChange(n);
  };
  useEffect(() => flush, []); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = (n: number, now: boolean) => {
    pending.current = n;
    setDraft(n);
    window.clearTimeout(timer.current);
    if (now) flush(); else timer.current = window.setTimeout(flush, 450);
  };

  const onKey = (e: React.KeyboardEvent, i: number) => {
    let next = i;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = Math.min(5, i + 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = Math.max(1, i - 1);
    else if (e.key === 'Home') next = 1;
    else if (e.key === 'End') next = 5;
    else return;
    e.preventDefault();
    pips.current[next - 1]?.focus();
    choose(next, false);
  };

  // Hovering a pip previews its level on the meter, so what a click does is visible first.
  const shown = peek ?? draft;

  return (
    <span className="flex min-w-0 items-center gap-2">
      <span
        role="radiogroup"
        aria-label="Spice"
        className="inline-flex flex-none items-center"
        onMouseLeave={() => setPeek(null)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) { flush(); setPeek(null); }
        }}
      >
        {SPICE.map((s) => {
          const i = s.level;
          return (
            <Tooltip key={i} label={`${s.label}: ${s.short}`} note={s.reference}>
              <button
                ref={(el) => { pips.current[i - 1] = el; }}
                type="button"
                role="radio"
                aria-checked={draft === i}
                aria-label={`${s.label}, ${s.short.toLowerCase()}`}
                tabIndex={draft === i || (draft < 1 && i === 1) ? 0 : -1}
                onClick={() => choose(i, true)}
                onKeyDown={(e) => onKey(e, i)}
                onMouseEnter={() => setPeek(i)}
                className={`flex h-6 w-5 items-center justify-center rounded-inner ${STATE} ${CONTROL_MOTION} ${FOCUS} ${TOUCH}`}
              >
                <span
                  aria-hidden="true"
                  className={`w-[5px] rounded-inner transition-[background-color,transform] duration-quick ease-emphasized ${
                    i <= shown ? PIP_ON[shown] : 'bg-outline-variant'
                  } ${shown === 5 && i === 5 ? 'h-[13px]' : 'h-[10px]'} ${peek !== null && i <= shown ? 'opacity-80' : ''} ${flamePip(i, shown === 5).className}`}
                  style={flamePip(i, shown === 5).style}
                />
              </button>
            </Tooltip>
          );
        })}
      </span>
      <SpiceText level={shown} />
    </span>
  );
};

export const SpiceReadOnly: React.FC<{ level: number }> = ({ level }) => (
  <span className="flex min-w-0 items-center gap-2">
    <SpiceMeter level={level} />
    <SpiceText level={level} />
  </span>
);

// ── Dates ────────────────────────────────────────────────────────────────────

const DateReadout: React.FC<{ value: string | null; open: boolean }> = ({ value, open }) => {
  if (!value) return <span className="text-sm text-on-surface-variant">Not set</span>;
  const h = whenText(value, open);
  return (
    <span className="truncate text-sm tabular-nums text-on-surface">
      {dayText(value)}
      <span className={h.overdue ? 'font-medium text-red-300' : 'text-on-surface-variant'}>, {h.text}</span>
    </span>
  );
};

export const EventLink: React.FC<{ id: string; what: string }> = ({ id, what }) => (
  <Tooltip label="Open the event to change it" note="This comes from the linked event.">
    <Link
      to={`/events/${id}`}
      aria-label={`Open the linked event, ${what}`}
      className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded-control text-on-surface-variant hover:text-on-surface ${STATE} ${CONTROL_MOTION} ${FOCUS} ${TOUCH}`}
    >
      <IconArrowUpRight className="h-4 w-4" />
    </Link>
  </Tooltip>
);

/** A date that reads as text. Enter or blur saves, Escape cancels. `note` is spoken, never drawn. */
export const DateValue: React.FC<{
  label: string;
  value: string | null;
  open: boolean;
  editable: boolean;
  note?: string;
  eventId?: string;
  onSave: (ymd: string | null) => void;
}> = ({ label, value, open, editable, note, eventId, onSave }) => (
  <KitDateValue
    label={label}
    value={value}
    editable={editable}
    note={note}
    speak={dayText}
    readout={(v) => <DateReadout value={v} open={open} />}
    readOnlyAfter={eventId ? <EventLink id={eventId} what={label} /> : undefined}
    onSave={onSave}
  />
);

// ── Drones ───────────────────────────────────────────────────────────────────

type DroneBody = { ls_drone_count?: number | null; pyro_drone_count?: number | null };

const toCount = (raw: string): number | null => (raw === '' ? null : Math.max(0, Math.floor(Number(raw))));

export const DroneCounts: React.FC<{
  ls: number | null;
  pyro: number | null;
  editable: boolean;
  eventId?: string;
  onSave: (body: DroneBody) => void;
}> = ({ ls, pyro, editable, eventId, onSave }) => {
  const [editing, setEditing] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const first = useRef<HTMLInputElement>(null);
  const second = useRef<HTMLInputElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const settled = useRef(false);
  const giveBack = useRef(false);

  useEffect(() => {
    if (editing) { settled.current = false; first.current?.focus(); first.current?.select(); }
    else if (giveBack.current) { giveBack.current = false; button.current?.focus(); }
  }, [editing]);

  const finish = (save: boolean, refocus: boolean) => {
    if (settled.current) return;
    settled.current = true;
    giveBack.current = refocus;
    const a = toCount(first.current?.value ?? '');
    const b = toCount(second.current?.value ?? '');
    setEditing(false);
    if (!save) return;
    const body: DroneBody = {};
    if (a !== ls) body.ls_drone_count = a;
    if (b !== pyro) body.pyro_drone_count = b;
    if (Object.keys(body).length) onSave(body);
  };

  const text = `${ls ?? 0} light show${pyro ? `, ${pyro} pyro` : ''}`;

  if (!editable) {
    return (
      <>
        <span className="truncate text-sm tabular-nums text-on-surface">{text}</span>
        {eventId && <EventLink id={eventId} what="drone counts" />}
      </>
    );
  }

  if (editing) {
    const onKey = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') { e.preventDefault(); finish(true, true); }
      else if (e.key === 'Escape') { e.preventDefault(); finish(false, true); }
    };
    return (
      <div
        ref={box}
        className="flex w-full gap-2 motion-safe:animate-menuIn"
        onBlur={(e) => { if (!box.current?.contains(e.relatedTarget as Node | null)) finish(true, false); }}
      >
        <Field ref={first} size="sm" aria-label="Light show drones" type="number" min={0} defaultValue={ls ?? ''} placeholder="Light show" onKeyDown={onKey} className="flex-1" />
        <Field ref={second} size="sm" aria-label="Pyro drones" type="number" min={0} defaultValue={pyro ?? ''} placeholder="Pyro" onKeyDown={onKey} className="flex-1" />
      </div>
    );
  }

  return (
    <button
      ref={button}
      type="button"
      aria-label={`Drones, ${text}, edit`}
      onClick={() => setEditing(true)}
      className={`group ${INLINE_VALUE}`}
    >
      <span className="truncate text-sm tabular-nums text-on-surface">{text}</span>
      <IconPencil className="h-3.5 w-3.5 flex-none text-on-surface-variant opacity-60 transition-opacity duration-quick ease-emphasized group-hover:opacity-100" />
    </button>
  );
};
